// Considered: raw <input> elements — rejected because schema fields must render with the
// same design-system atoms (and per-type behavior) as the Sphere Editor. This mirrors
// AdditionalPropertiesGrid.tsx (both consume the shared `resolveRenderer`/`UnifiedPropertyRendererMolecule`
// chain — EAINT-855), but is Formik-agnostic (plain value/onChange) so it can be used in
// lightweight forms like the Add Sphere dialog's Configure step.
//
// Labelling follows the Sphere Editor's convention: the caller renders `<label htmlFor={property.name}>`
// and every control this molecule mounts carries `id={property.name}` (the renderer adapters, and
// the Dropdown's `inputId` below). The reference and date-time pickers own their input ids, so
// they are named from the property directly.
import React from 'react';
import type { SchemaProperty } from 'types/schema';
import type { RenderMode } from 'types/aspect';
import DateTimePickerInputAtom from '../../atoms/DateTimePickerInput/DateTimePickerInputAtom';
import UrgencyPillMolecule from '../UrgencyPill/UrgencyPill';
import TextEditorAtom from '../../atoms/TextEditor/TextEditorAtom';
import SphereReferenceInputMolecule from '../SphereReference/SphereReferenceInputMolecule';
import DropdownAtom, { DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import { getSelectOptions } from '../../../utils/schemaPropertyOptions';
import UnifiedPropertyRendererMolecule from '../UnifiedPropertyRenderer/UnifiedPropertyRendererMolecule';
import { resolveRenderer } from '../../../utils/resolveRenderer';
import { numberRuleConstraints } from '../../../utils/numberRuleConstraints';
import './SphereFieldInputMolecule.scss';

export interface SphereFieldInputMoleculeProps {
  property: SchemaProperty;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  className?: string;
  /** Current values of sibling properties — forwarded to reference inputs so $value(<sibling>) cascade placeholders resolve. */
  siblingValues?: Record<string, any>;
  /** Org the edited sphere belongs to — forwarded to reference inputs so candidates resolve in that org, not the caller's home org. */
  organizationId?: string;
}

const SphereFieldInputMolecule: React.FC<SphereFieldInputMoleculeProps> = ({
  property,
  value,
  onChange,
  disabled = false,
  invalid = false,
  placeholder,
  className = '',
  siblingValues,
  organizationId,
}) => {
  // Urgency uses the dedicated level selector (same control as SphereProperties), not a
  // generic number stepper. The schema marks it with component='UrgencySelector'.
  if (property.name === 'urgency' || (property as any).component === 'UrgencySelector') {
    const numeric = typeof value === 'number' ? value : Number(value);
    return (
      <div className="sphere-field-input__urgency">
        <UrgencyPillMolecule
          urgencyValue={Number.isFinite(numeric) ? numeric : 3}
          onUrgencyChange={(u) => onChange(Number(u))}
        />
      </div>
    );
  }

  // Description uses the rich-text editor (same as SphereProperties), not a plain input.
  if (property.name === 'description') {
    return (
      <TextEditorAtom
        value={typeof value === 'string' ? value : ''}
        onChange={(v) => onChange(v)}
        disabled={disabled}
        height="160px"
      />
    );
  }

  // Select / enum: a string property with a fixed option set renders as a dropdown,
  // mirroring AdditionalPropertiesGrid via the shared getSelectOptions so both derive options
  // identically — the resolver's code-defined type-default table has no concept of per-property
  // enum options, so this (like `reference` and `datetime` below) bypasses it. An aspect's
  // explicit `renderers`/legacy `editComponent` for this property still wins (checked first).
  const mode: RenderMode = 'edit';
  const resolved = resolveRenderer(property, mode);

  if (resolved.source === 'typeDefault') {
    const selectOptions = getSelectOptions(property);
    if (selectOptions) {
      return (
        <DropdownAtom
          inputId={property.name}
          value={value ?? null}
          onChange={(e: DropdownChangeEvent) => onChange(e.value)}
          options={selectOptions}
          optionLabel="label"
          optionValue="value"
          placeholder={placeholder}
          ariaLabel={property.label || property.name}
          disabled={disabled}
          // w-full: PrimeReact's .p-dropdown is inline and sizes to content, so in the
          // flex-column field it collapses to a tiny circle. Stretch it full-width to
          // match the sibling inputs — same approach the Sphere editor uses. The
          // molecule class aligns its height to the 40px inputs (DropdownAtom's label
          // padding otherwise renders it taller than the sibling text/number fields).
          className={`sphere-field-input__dropdown w-full ${className}`.trim()}
        />
      );
    }

    if (property.type === 'reference') {
      return (
        <SphereReferenceInputMolecule
          property={property}
          value={value ?? null}
          onChange={(id) => onChange(id ?? null)}
          disabled={disabled}
          editable={true}
          className={className}
          ariaLabel={property.label || property.name}
          siblingValues={siblingValues}
          organizationId={organizationId}
        />
      );
    }

    if (property.type === 'datetime') {
      return (
        <DateTimePickerInputAtom
          value={value ?? null}
          onChange={(e: any) => onChange(e?.target?.value ?? e ?? null)}
          dateFormat={(property as any).dateFormat || 'MM/dd/yy'}
          disabled={disabled}
          ariaLabel={property.label || property.name}
          className={className}
        />
      );
    }
  }

  // `editable` alongside `disabled`: legacy components mounted through the shim
  // (`SchedulesEditorMolecule`, `ChipListDisplayAtom`) branch on `editable`, not `disabled`, so
  // without it a legacy component renders in its editable state in a disabled host — the same
  // cross-surface drift SC5 exists to prevent, and the reason `AdditionalPropertiesGrid` passes it
  // on every property type. This molecule newly routes legacy `editComponent` properties through
  // the shim (it previously ignored them), so it needs the same treatment.
  const editable = !disabled;
  const extraProps: Record<string, unknown> | undefined =
    property.type === 'number'
      ? (() => {
          const { min, max, integer, precision } = numberRuleConstraints(property);
          return { numberType: integer ? 'integer' : 'decimal', min, max, precision, showButtons: true, invalid, disabled, editable };
        })()
      : property.type === 'unit'
        ? (() => {
            const { min, max, integer, precision } = numberRuleConstraints(property);
            const unitConfig = (property as any).unitConfig || { category: 'time', defaultUnit: 'h', showSwitcher: true, displayPrecision: 2 };
            return { min, max, integer, precision, unitConfig, showStepper: (property as any).showStepper !== false, invalid, disabled, editable };
          })()
        : resolved.source === 'typeDefault' && property.type === 'date'
          ? // Same hardcoded picker config `AdditionalPropertiesGrid.extraPropsFor` gives the `DateField`
            // catalog adapter for `date`-edit — both surfaces now route the SAME component
            // (`DateFieldAdapter`/`DatePickerAtom`), not divergent bespoke atoms (M-7, SC5).
            { dateFormat: 'yy-mm-dd', selectionMode: 'single' as const, disabled, invalid, placeholder, className, editable }
          : { disabled, invalid, placeholder, className, editable };

  // A `{type:'multiField'}` legacy `changeMapper`/`valueAccessor` coordinates several sibling
  // form fields (e.g. a schedule's start/end) — meaningless here, since this molecule edits one
  // isolated property with no sibling-field bag to fan a multi-field payload out to (unlike
  // AdditionalPropertiesGrid's Formik context). Its declared components are therefore mounted
  // with the plain single-value contract; a genuinely multiField-configured property isn't a
  // realistic input to this molecule's callers (e.g. the Add Sphere dialog).
  return (
    <UnifiedPropertyRendererMolecule
      property={property}
      mode={mode}
      value={value}
      onChange={onChange}
      extraProps={extraProps}
      className={className}
    />
  );
};

export default SphereFieldInputMolecule;
